/**
 * RhMonPlanningPanel — le planning individuel, dans l'espace personnel RH.
 *
 * DÉCISION (23/07) : le planning individuel vit ICI, pas dans chaque brique.
 * Tout le monde — agent, manager, RH, maire — voit le sien au même endroit, avec
 * ses demandes juste à côté. Conséquences :
 *   · le service `rh`, seul des 8 à n'avoir aucune table de créneaux, n'en a
 *     plus besoin : ses agents voient leur planning comme les autres ;
 *   · une seule vue individuelle riche à maintenir, au lieu de la dupliquer
 *     dans les 6 briques.
 * Les vues « Mon planning » des briques RESTENT en consultation : on ne
 * supprime rien.
 *
 * Ce que l'agent peut faire ici : cliquer un créneau (ou sélectionner des jours)
 * pour DÉCLARER une absence. La déclaration entre dans le workflow de validation
 * existant (mig 196, réglage commune manager | rh_maire) — non modifié.
 * Il ne modifie ni ne supprime les créneaux : ça reste au manager.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiException } from '@/lib/api';
import { Button, Card, Badge } from '@/components/ui';
import { CalendarDays, ChevronLeft, ChevronRight, CalendarPlus, Info, Download } from 'lucide-react';
import type { RhCreneauAgrege, PlanningAnneeResponse } from '@roronoa-collectivites/shared-types';
import { RhDeclarerAbsenceModal } from './RhDeclarerAbsenceModal';

const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const MOIS_COURTS = ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];

/**
 * Couleurs de la grille année. Mêmes teintes que l'export PDF, pour qu'un agent
 * qui imprime retrouve exactement ce qu'il voit à l'écran.
 */
const STATUT_STYLE: Record<string, { cls: string; libelle: string }> = {
  present:      { cls: 'bg-emerald-500/25 text-emerald-200', libelle: 'Présence' },
  demi:         { cls: 'bg-emerald-500/15 text-emerald-200', libelle: 'Demi-journée' },
  conge:        { cls: 'bg-sky-500/25 text-sky-200',         libelle: 'Congés' },
  absence:      { cls: 'bg-rose-500/25 text-rose-200',       libelle: 'Absence' },
  ferie:        { cls: 'bg-amber-500/25 text-amber-200',     libelle: 'Férié' },
  weekend:      { cls: 'bg-surface-muted/40 text-ink-dim',   libelle: 'Week-end' },
  non_planifie: { cls: '',                                   libelle: 'Non planifié' },
};

const SERVICE_LABEL: Record<string, string> = {
  service_technique: 'Technique', scolaire_cantine: 'Scolaire', grc: 'Communication',
  petite_enfance: 'Petite enfance', urbanisme: 'Urbanisme', comptable: 'Comptabilité',
};

function lundiDe(d: Date): string {
  const x = new Date(d);
  const jour = (x.getDay() + 6) % 7; // lundi = 0
  x.setDate(x.getDate() - jour);
  return x.toISOString().slice(0, 10);
}

function heure(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
  }).format(new Date(iso));
}

function dateCourte(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric', month: 'short', timeZone: 'Europe/Paris',
  }).format(new Date(iso));
}

export function RhMonPlanningPanel() {
  const [vue, setVue] = useState<'semaine' | 'mois' | 'annee'>('semaine');
  const [weekStart, setWeekStart] = useState(() => lundiDe(new Date()));
  const [creneaux, setCreneaux] = useState<RhCreneauAgrege[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Jours sélectionnés (YYYY-MM-DD) pour déclarer une absence en une fois.
  const [selection, setSelection] = useState<string[]>([]);
  const [modalOuvert, setModalOuvert] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.rh.monPlanning(weekStart);
      setCreneaux(res.creneaux);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Chargement impossible');
    } finally { setLoading(false); }
  }, [weekStart]);

  useEffect(() => { void charger(); }, [charger]);

  /** Les 7 jours de la semaine, avec leurs créneaux. */
  const jours = useMemo(() => {
    const base = new Date(`${weekStart}T00:00:00`);
    return JOURS.map((nom, i) => {
      const d = new Date(base);
      d.setDate(d.getDate() + i);
      const iso = d.toISOString().slice(0, 10);
      return {
        nom, iso,
        creneaux: creneaux
          .filter((c) => c.dateDebut.slice(0, 10) === iso)
          .sort((a, b) => a.dateDebut.localeCompare(b.dateDebut)),
      };
    });
  }, [creneaux, weekStart]);

  const totalHeures = useMemo(
    () => creneaux.filter((c) => c.statut !== 'absent').reduce((n, c) => n + (c.dureeHeures || 0), 0),
    [creneaux]
  );

  function decalerSemaine(sens: -1 | 1) {
    const d = new Date(`${weekStart}T00:00:00`);
    d.setDate(d.getDate() + sens * 7);
    setWeekStart(d.toISOString().slice(0, 10));
    setSelection([]);
  }

  function basculerJour(iso: string) {
    setSelection((prev) => prev.includes(iso) ? prev.filter((x) => x !== iso) : [...prev, iso]);
  }

  const finSemaine = useMemo(() => {
    const d = new Date(`${weekStart}T00:00:00`);
    d.setDate(d.getDate() + 6);
    return d.toISOString().slice(0, 10);
  }, [weekStart]);

  return (
    <div className="space-y-4">
      {/*
        Sélecteur de vue. La SEMAINE reste la vue d'entrée : c'est là que se
        déclarent les absences (sélection multi-jours + modale), et c'est le
        geste quotidien. L'ANNÉE est la vue de recul — compteurs, congés posés,
        fériés d'un coup d'œil — et s'exporte en PDF.

        La vue année n'existe QUE dans l'espace RH : les briques gardent une
        consultation semaine/équipe, sans dupliquer ce récapitulatif.
      */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md ring-1 ring-line" role="tablist" aria-label="Vue du planning">
          <button
            type="button" role="tab" aria-selected={vue === 'semaine'}
            onClick={() => setVue('semaine')}
            className={`rounded-l-md px-3 py-1.5 text-sm ${vue === 'semaine' ? 'bg-rh text-white' : 'text-ink-muted hover:text-ink'}`}
          >
            Semaine
          </button>
          <button
            type="button" role="tab" aria-selected={vue === 'mois'}
            onClick={() => setVue('mois')}
            className={`px-3 py-1.5 text-sm ${vue === 'mois' ? 'bg-rh text-white' : 'text-ink-muted hover:text-ink'}`}
          >
            Mois
          </button>
          <button
            type="button" role="tab" aria-selected={vue === 'annee'}
            onClick={() => setVue('annee')}
            className={`rounded-r-md px-3 py-1.5 text-sm ${vue === 'annee' ? 'bg-rh text-white' : 'text-ink-muted hover:text-ink'}`}
          >
            Année
          </button>
        </div>
      </div>

      {vue === 'annee' ? (
        <RhMonAnneeVue />
      ) : vue === 'mois' ? (
        <RhMonMoisVue />
      ) : (
      <>
      {/* Barre semaine */}
      <Card variant="glass" padded>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => decalerSemaine(-1)} aria-label="Semaine précédente">
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <div className="text-center">
              <p className="flex items-center gap-2 text-sm font-medium text-ink">
                <CalendarDays className="h-4 w-4 text-rh-fg" aria-hidden="true" />
                Semaine du {dateCourte(weekStart)} au {dateCourte(finSemaine)}
              </p>
              <p className="text-xs text-ink-subtle">
                {totalHeures.toFixed(1).replace('.', ',')} h planifiées
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => decalerSemaine(1)} aria-label="Semaine suivante">
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>

          <Button
            variant="primary" size="sm"
            onClick={() => setModalOuvert(true)}
          >
            <CalendarPlus className="h-4 w-4" aria-hidden="true" />
            {selection.length > 0
              ? `Déclarer une absence (${selection.length} jour${selection.length > 1 ? 's' : ''})`
              : 'Déclarer une absence'}
          </Button>
        </div>
      </Card>

      {error && <p className="text-sm text-danger">{error}</p>}

      <p className="flex items-start gap-2 text-xs text-ink-subtle">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Sélectionnez un ou plusieurs jours puis « Déclarer une absence ». Votre demande
        part en validation selon les règles de la commune.
      </p>

      {loading ? (
        <Card variant="glass" padded>
          <p className="py-6 text-center text-sm text-ink-subtle">Chargement…</p>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {jours.map((j) => {
            const choisi = selection.includes(j.iso);
            return (
              <button
                key={j.iso}
                type="button"
                onClick={() => basculerJour(j.iso)}
                aria-pressed={choisi}
                className={`rounded-r-lg border p-3 text-left transition-colors ${
                  choisi
                    ? 'border-rh bg-rh-tint/30'
                    : 'border-border bg-surface/40 hover:border-rh/40'
                }`}
              >
                <div className="mb-2 flex items-baseline justify-between">
                  <span className="text-sm font-medium text-ink">{j.nom}</span>
                  <span className="text-2xs text-ink-dim">{dateCourte(j.iso)}</span>
                </div>

                {j.creneaux.length === 0 ? (
                  <p className="text-xs text-ink-dim">—</p>
                ) : (
                  <div className="space-y-1.5">
                    {j.creneaux.map((c) => (
                      <div
                        key={c.id}
                        className={`rounded-r-sm px-2 py-1.5 text-xs ${
                          c.statut === 'absent'
                            ? 'bg-surface/60 text-ink-dim line-through'
                            : 'bg-rh-tint/50 text-ink'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span className="font-medium">
                            {heure(c.dateDebut)}–{heure(c.dateFin)}
                          </span>
                          {c.statut === 'absent' && (
                            <Badge variant="neutral" size="sm">Absent</Badge>
                          )}
                        </div>
                        {c.titre && <p className="truncate">{c.titre}</p>}
                        <p className="text-2xs text-ink-dim">
                          {SERVICE_LABEL[c.service] ?? c.service}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {modalOuvert && (
        <RhDeclarerAbsenceModal
          joursPreselectionnes={selection}
          onClose={() => setModalOuvert(false)}
          onDeclare={() => { setModalOuvert(false); setSelection([]); void charger(); }}
        />
      )}
      </>
      )}
    </div>
  );
}

/**
 * RhMonAnneeVue — la vue année de MON planning, dans l'espace RH personnel.
 *
 * Grille PIVOTÉE : 31 lignes (jours) × 12 colonnes (mois). L'orientation est
 * volontaire — elle tient en portrait sur mobile, là où 12 lignes × 31 colonnes
 * imposerait un défilement horizontal. C'est la forme qui existait avant et que
 * la mairie utilisait ; on la reprend telle quelle.
 *
 * Source : `/api/rh/mon-annee` — agrégé TOUTES BRIQUES (vue `rh_creneaux_paie`).
 * L'agent voit donc son année complète, cantine + technique + accueil confondus,
 * ce qu'aucune vue de brique ne peut montrer.
 */
function RhMonAnneeVue() {
  const [annee, setAnnee] = useState(() => new Date().getFullYear());
  const [data, setData] = useState<PlanningAnneeResponse | null>(null);
  const [compteurs, setCompteurs] = useState<Array<{ type: string; libelle: string; solde: number; unite: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let vivant = true;
    setLoading(true); setError(null);
    api.rh.monAnnee(annee)
      .then((r) => { if (vivant) setData(r); })
      .catch((e) => { if (vivant) setError(e instanceof ApiException ? e.message : 'Chargement impossible'); })
      .finally(() => { if (vivant) setLoading(false); });
    api.rh.mesCompteurs(annee)
      .then((c) => { if (vivant) setCompteurs(c.map((x) => ({ type: x.type, libelle: x.libelle, solde: x.solde, unite: x.unite }))); })
      .catch(() => { /* les compteurs sont un complément : leur absence ne casse pas la vue */ });
    return () => { vivant = false; };
  }, [annee]);

  const parJour = useMemo(
    () => new Map((data?.jours ?? []).map((j) => [j.date, j])),
    [data],
  );

  async function exporterPdf() {
    setExporting(true);
    try { await api.rh.telechargerMonAnneePdf(annee); }
    catch { setError('Export impossible'); }
    finally { setExporting(false); }
  }

  return (
    <div className="space-y-4">
      <Card variant="glass" padded>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setAnnee((a) => a - 1)} aria-label="Année précédente">
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <p className="flex items-center gap-2 text-sm font-medium text-ink tabular-nums">
              <CalendarDays className="h-4 w-4 text-rh-fg" aria-hidden="true" />
              {annee}
            </p>
            <Button variant="ghost" size="sm" onClick={() => setAnnee((a) => a + 1)} aria-label="Année suivante">
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
          <Button variant="secondary" size="sm" onClick={() => void exporterPdf()} disabled={exporting || loading}>
            <Download className="h-4 w-4" aria-hidden="true" />
            {exporting ? 'Export…' : 'Exporter en PDF'}
          </Button>
        </div>
      </Card>

      {error && <p className="text-sm text-danger">{error}</p>}

      {/* Légende — sans elle, la grille de couleurs n'est pas lisible. */}
      <div className="flex flex-wrap gap-3">
        {(['present', 'conge', 'absence', 'ferie', 'weekend'] as const).map((k) => (
          <span key={k} className="flex items-center gap-1.5 text-xs text-ink-muted">
            <span className={`inline-block h-3 w-3 rounded-sm ring-1 ring-line ${STATUT_STYLE[k]!.cls}`} />
            {STATUT_STYLE[k]!.libelle}
          </span>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-ink-subtle">Chargement…</p>
      ) : (
        <Card variant="glass" padded>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-2xs">
              <thead>
                <tr>
                  <th className="w-6 p-1 text-left font-normal text-ink-dim" />
                  {MOIS_COURTS.map((m) => (
                    <th key={m} className="p-1 text-center font-semibold text-ink-muted">{m}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((jour) => (
                  <tr key={jour}>
                    <td className="p-0.5 text-right tabular-nums text-ink-dim">{jour}</td>
                    {MOIS_COURTS.map((_, mois) => {
                      const nbJours = new Date(annee, mois + 1, 0).getDate();
                      if (jour > nbJours) return <td key={mois} className="p-0.5" />;
                      const iso = `${annee}-${String(mois + 1).padStart(2, '0')}-${String(jour).padStart(2, '0')}`;
                      const j = parJour.get(iso);
                      const st = STATUT_STYLE[j?.statut ?? 'non_planifie'] ?? STATUT_STYLE.non_planifie!;
                      return (
                        <td key={mois} className="p-0.5">
                          <div
                            className={`h-4 rounded-sm ring-1 ring-line/50 ${st.cls}`}
                            title={`${jour} ${MOIS_COURTS[mois]} — ${j?.libelle || st.libelle}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Compteurs : présence/congés/absences de l'année + soldes RH (RTT, récup). */}
      {data && (
        <Card variant="glass" padded>
          <p className="mb-2 text-sm font-semibold text-ink">Compteurs {annee}</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <div><p className="text-xs text-ink-muted">Jours de présence</p><p className="text-lg font-bold text-ink tabular-nums">{data.compteurs.presence}</p></div>
            <div><p className="text-xs text-ink-muted">Jours de congés</p><p className="text-lg font-bold text-ink tabular-nums">{data.compteurs.conges}</p></div>
            <div><p className="text-xs text-ink-muted">Jours d'absence</p><p className="text-lg font-bold text-ink tabular-nums">{data.compteurs.absences}</p></div>
          </div>
          {compteurs.length > 0 && (
            <div className="mt-3 grid gap-3 border-t border-line pt-3 sm:grid-cols-3">
              {compteurs.map((c) => (
                <div key={c.type}>
                  <p className="text-xs text-ink-muted">{c.libelle}</p>
                  <p className="text-lg font-bold text-ink tabular-nums">
                    {String(c.solde).replace('.', ',')} <span className="text-xs font-normal text-ink-muted">{c.unite}</span>
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

/**
 * RhMonMoisVue — MON planning au mois, façon calendrier.
 *
 * Vue intermédiaire entre la semaine (le détail des créneaux) et l'année (la
 * vue d'ensemble compressée) : une grille de calendrier classique, lisible,
 * où l'on fait défiler les mois. Même code couleur que la vue année, pour
 * qu'un même statut se reconnaisse partout de la même façon.
 *
 * Source : `/api/rh/mon-annee` — le même agrégat TOUTES BRIQUES que la vue
 * année, filtré sur le mois affiché. Un seul appel par année, donc changer de
 * mois à l'intérieur d'une même année ne relance aucune requête.
 */
function RhMonMoisVue() {
  const [curseur, setCurseur] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [data, setData] = useState<PlanningAnneeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const annee = curseur.getFullYear();
  const mois = curseur.getMonth();

  useEffect(() => {
    let vivant = true;
    setLoading(true); setError(null);
    api.rh.monAnnee(annee)
      .then((r) => { if (vivant) setData(r); })
      .catch((e) => { if (vivant) setError(e instanceof ApiException ? e.message : 'Chargement impossible'); })
      .finally(() => { if (vivant) setLoading(false); });
    return () => { vivant = false; };
  }, [annee]);

  const parJour = useMemo(
    () => new Map((data?.jours ?? []).map((j) => [j.date, j])),
    [data],
  );

  // Grille du mois alignée sur lundi : on complète le début avec les cases
  // vides des jours du mois précédent.
  const cases = useMemo(() => {
    const premier = new Date(annee, mois, 1);
    const dow = premier.getDay() === 0 ? 7 : premier.getDay(); // 1 = lundi
    const nbJours = new Date(annee, mois + 1, 0).getDate();
    const out: (number | null)[] = Array.from({ length: dow - 1 }, () => null);
    for (let j = 1; j <= nbJours; j++) out.push(j);
    return out;
  }, [annee, mois]);

  const libelleMois = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(curseur);

  return (
    <div className="space-y-4">
      <Card variant="glass" padded>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setCurseur(new Date(annee, mois - 1, 1))} aria-label="Mois précédent">
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <p className="flex items-center gap-2 text-sm font-medium capitalize text-ink">
              <CalendarDays className="h-4 w-4 text-rh-fg" aria-hidden="true" />
              {libelleMois}
            </p>
            <Button variant="ghost" size="sm" onClick={() => setCurseur(new Date(annee, mois + 1, 1))} aria-label="Mois suivant">
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </Card>

      {error && <p className="text-sm text-danger">{error}</p>}

      <div className="flex flex-wrap gap-3">
        {(['present', 'conge', 'absence', 'ferie', 'weekend'] as const).map((k) => (
          <span key={k} className="flex items-center gap-1.5 text-xs text-ink-muted">
            <span className={`inline-block h-3 w-3 rounded-sm ring-1 ring-line ${STATUT_STYLE[k]!.cls}`} />
            {STATUT_STYLE[k]!.libelle}
          </span>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-ink-subtle">Chargement…</p>
      ) : (
        <Card variant="glass" padded>
          <div className="grid grid-cols-7 gap-1">
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((j) => (
              <div key={j} className="pb-1 text-center text-2xs font-semibold uppercase tracking-r-wide text-ink-muted">{j}</div>
            ))}
            {cases.map((jour, i) => {
              if (jour === null) return <div key={`v${i}`} />;
              const iso = `${annee}-${String(mois + 1).padStart(2, '0')}-${String(jour).padStart(2, '0')}`;
              const j = parJour.get(iso);
              const st = STATUT_STYLE[j?.statut ?? 'non_planifie'] ?? STATUT_STYLE.non_planifie!;
              return (
                <div
                  key={iso}
                  className={`min-h-[68px] rounded-md p-1.5 ring-1 ring-line/60 ${st.cls}`}
                  title={j?.libelle || st.libelle}
                >
                  <div className="text-xs font-semibold tabular-nums">{jour}</div>
                  {j?.libelle && (
                    <div className="mt-0.5 line-clamp-2 text-2xs leading-tight opacity-90">{j.libelle}</div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {data && (
        <Card variant="glass" padded>
          <p className="mb-2 text-sm font-semibold text-ink">Compteurs {annee}</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <div><p className="text-xs text-ink-muted">Jours de présence</p><p className="text-lg font-bold text-ink tabular-nums">{data.compteurs.presence}</p></div>
            <div><p className="text-xs text-ink-muted">Jours de congés</p><p className="text-lg font-bold text-ink tabular-nums">{data.compteurs.conges}</p></div>
            <div><p className="text-xs text-ink-muted">Jours d'absence</p><p className="text-lg font-bold text-ink tabular-nums">{data.compteurs.absences}</p></div>
          </div>
        </Card>
      )}
    </div>
  );
}
